import { useMemo } from 'react';

import {
    useGetDepositLimits,
    useGetForcedLossLimits,
    useGetGameHistory,
    useGetLossLimits,
    useGetPlayerLimitsInfo,
    useGetPlayerSelfExclusion,
    useSetDepositLimits,
    useSetPlayerSelfExclusion
} from 'server';

export const useResponsibleGamingPublicAPI = () => {
    const getDepositLimits = useGetDepositLimits();
    const getForcedLossLimits = useGetForcedLossLimits();
    const getGameHistory = useGetGameHistory();
    const getLossLimits = useGetLossLimits();
    const getPlayerLimitsInfo = useGetPlayerLimitsInfo();
    const getPlayerSelfExclusion = useGetPlayerSelfExclusion();
    const setDepositLimits = useSetDepositLimits();
    const setPlayerSelfExclusion = useSetPlayerSelfExclusion();

    return useMemo(() => ({
        /**
         * Return information about current deposit limits.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{currentLimits: object, waitingLimits: object, allowedLimits: string[]}>}
         * @example
         * Playtech.API.responsibleGaming.getDepositLimits();
         */
        getDepositLimits,
        /**
         * Return information about current loss limits.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{limits: object}>}
         * @example
         * Playtech.API.responsibleGaming.getLossLimits();
         */
        getLossLimits,
        /**
         * Return information about current forced loss limits.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{limits: object}>}
         * @example
         * Playtech.API.responsibleGaming.getForcedLossLimits();
         */
        getForcedLossLimits,
        /**
         * Return information about current self-exclusion.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{allowed: {boolean}, disableDeposits: {boolean}, crossProduct: {boolean}, products: {string[]}, periods: {string[]}, endTypes: {string[]}, panicButtonInterval: {number}, minEndDate: {string}, productSettings: {object[]}}>}
         * @example
         * Playtech.API.responsibleGaming.getPlayerSelfExclusion();
         */
        getPlayerSelfExclusion,
        /**
         * Return information about all current limits.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{object}>}
         * @example
         * Playtech.API.responsibleGaming.getPlayerLimitsInfo();
         */
        getPlayerLimitsInfo,
        /**
         * Set player's deposit limits.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<void>}
         * @param request {Object}
         * @param [request.amount] {Number}
         * @param [request.timePeriod] {String}
         * @param [limits] {Object}
         * @example
         * Playtech.API.responsibleGaming.setDepositLimits();
         */
        setDepositLimits,
        /**
         * Set player's self exclusion.
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<void>}
         * @param request {Object}
         * @param [request.period] {String}
         * @param [request.endDate] {String}
         * @param [request.product] {String}
         * @param [request.endType] {String}
         * @param [request.endSelfExclusion] {String}
         * @param [request.startDate] {String}
         * @param [request.emailNotification] {Number}
         * @param [request.networkClass] {Number}
         * @example
         * Playtech.API.responsibleGaming.setPlayerSelfExclusion();
         */
        setPlayerSelfExclusion,
        /**
         *
         * @memberOf Playtech.API.responsibleGaming
         * @function
         * @returns {Promise<{object}>}
         * @example
         * Playtech.API.responsibleGaming.getGameHistory();
         */
        getGameHistory
    }), [
        getDepositLimits,
        getForcedLossLimits,
        getGameHistory,
        getLossLimits,
        getPlayerLimitsInfo,
        getPlayerSelfExclusion,
        setDepositLimits,
        setPlayerSelfExclusion
    ]);
};
